import React from 'react';
import { withRouter } from 'react-router-dom';
import { Card, CardContent, Grid, Typography } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { Skeleton } from '@material-ui/lab';

import MobileCampaign from './cards/MobileCampaign.jsx';

import { campaignActions } from '../../actions/campaign.js';
import { connect } from 'react-redux';

import NoCampaign from '../ux/NoCampaign';
import AccountChecklist from './AccountChecklist';

const ACCENT = '#111936';

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'live', label: 'Live' },
  { key: 'completed', label: 'Completed' },
];

const styles = () => ({
  page: {
    width: '100%',
    padding: '16px 12px 90px',
    boxSizing: 'border-box',
  },
  title: {
    fontFamily: "'Lato', sans-serif",
    fontWeight: 700,
    fontSize: 19,
    color: '#111827',
  },
  subtitle: {
    fontFamily: "'Lato', sans-serif",
    fontSize: 12.5,
    color: '#6B7280',
    marginTop: 2,
  },
  filterRow: {
    display: 'flex',
    overflowX: 'auto',
    margin: '14px 0 16px',
    paddingBottom: 2,
    '-webkit-overflow-scrolling': 'touch',
  },
  filterPill: {
    fontFamily: "'Lato', sans-serif",
    fontSize: 12.5,
    fontWeight: 700,
    borderRadius: 999,
    padding: '6px 14px',
    border: '1px solid #E5E7EB',
    background: 'white',
    color: '#4B5563',
    cursor: 'pointer',
    marginRight: 8,
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  filterPillActive: {
    background: ACCENT,
    borderColor: ACCENT,
    color: 'white',
  },
  emptyCard: {
    borderRadius: 12,
    border: '1px dashed #D1D5DB',
    boxShadow: 'none',
    textAlign: 'center',
  },
  emptyText: {
    fontFamily: "'Lato', sans-serif",
    fontSize: 13.5,
    color: '#6B7280',
  },
  clearLink: {
    fontFamily: "'Lato', sans-serif",
    fontSize: 13,
    fontWeight: 700,
    color: ACCENT,
    cursor: 'pointer',
    marginTop: 6,
    display: 'inline-block',
  },
});

function matchesFilter(campaign, filter) {
  if (filter === 'live') return Boolean(campaign.is_live);
  if (filter === 'completed') return campaign.status === 'completed';
  return true;
}

function CardSkeleton() {
  return (
    <Card
      elevation={0}
      style={{ marginBottom: 14, borderRadius: 12, border: '1px solid #E5E7EB' }}
    >
      <CardContent style={{ padding: '14px 16px' }}>
        <Grid container spacing={1} alignItems="center">
          <Grid item>
            <Skeleton variant="rect" width={40} height={40} style={{ borderRadius: 10 }} />
          </Grid>
          <Grid item xs>
            <Skeleton width="60%" height={20} />
            <Skeleton width="40%" height={14} />
          </Grid>
        </Grid>
        <Skeleton width="95%" style={{ marginTop: 12 }} />
        <Grid container spacing={1} style={{ marginTop: 8 }}>
          {[0, 1, 2, 3].map((i) => (
            <Grid item xs={6} key={i}>
              <Skeleton width="55%" height={18} />
              <Skeleton width="40%" height={12} />
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );
}

class MobileCampaignDashboard extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      filter: 'all',
    };
  }

  componentDidMount() {
    this.props.getInfluencerCampaigns();
  }

  render() {
    const { classes, campaigns, loading } = this.props;
    const { filter } = this.state;

    const all = campaigns || [];
    const counts = {
      all: all.length,
      live: all.filter((c) => matchesFilter(c, 'live')).length,
      completed: all.filter((c) => matchesFilter(c, 'completed')).length,
    };
    const visible = all.filter((c) => matchesFilter(c, filter));

    return (
      <div className={classes.page}>
        <Typography className={classes.title}>Your campaigns</Typography>
        <Typography className={classes.subtitle}>
          Track every campaign you are part of, from going live to getting paid.
        </Typography>

        <AccountChecklist />

        <div className={classes.filterRow}>
          {FILTERS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              className={`${classes.filterPill} ${
                filter === key ? classes.filterPillActive : ''
              }`}
              onClick={() => this.setState({ filter: key })}
            >
              {label}
              {counts[key] ? ` (${counts[key]})` : ''}
            </button>
          ))}
        </div>

        {loading ? (
          <>
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
          </>
        ) : all.length === 0 ? (
          <NoCampaign />
        ) : visible.length === 0 ? (
          <Card className={classes.emptyCard}>
            <CardContent style={{ padding: '30px 16px' }}>
              <Typography className={classes.emptyText}>
                No campaigns under “{FILTERS.find((f) => f.key === filter).label}”.
              </Typography>
              <span
                className={classes.clearLink}
                role="button"
                tabIndex={0}
                onClick={() => this.setState({ filter: 'all' })}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') this.setState({ filter: 'all' });
                }}
              >
                Show all campaigns
              </span>
            </CardContent>
          </Card>
        ) : (
          visible.map((campaign) => <MobileCampaign {...campaign} key={campaign.id} />)
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  loading: state.campaign.loading,
  campaigns: state.campaign.campaigns,
});

const actionCreators = {
  getInfluencerCampaigns: campaignActions.getInfluencerCampaigns,
};

export default connect(
  mapStateToProps,
  actionCreators,
)(withRouter(withStyles(styles)(MobileCampaignDashboard)));
